import React, { useMemo, useState } from 'react';
import { ComboBox, type ComboBoxOption } from '@lmt/signal-viewer-ui';
import { useFileSession } from '../contexts/FileSessionContext';
import { useNotifications } from '../components/notifications/NotificationProvider';
import {
  showFileUploadErrorNotification,
  showUnmatchedEventFilesNotificationIfNeeded,
} from './upload-notifications';
import {
  buildDemoUploadData,
  DEMO_FILES,
  findSelectedDemoFileId,
} from '../utils/demo-files';
import './DemoFileSelector.css';

export const DemoFileSelector: React.FC = () => {
  const { currentFileNames, onUploadFiles } = useFileSession();
  const notifications = useNotifications();
  const [isLoading, setIsLoading] = useState(false);

  const options = useMemo<ComboBoxOption[]>(
    () => DEMO_FILES.map((demo) => ({ value: demo.id, label: demo.label })),
    [],
  );

  // Derived from the file session so the selection clears when the user loads own files.
  const selectedId = findSelectedDemoFileId(currentFileNames) ?? '';

  const handleChange = async (id: string) => {
    const demo = DEMO_FILES.find((entry) => entry.id === id);
    if (!demo || id === selectedId || isLoading) return;

    setIsLoading(true);
    try {
      const data = await buildDemoUploadData(demo);
      showUnmatchedEventFilesNotificationIfNeeded(notifications, data);
      await onUploadFiles(data);
    } catch (error) {
      showFileUploadErrorNotification(notifications, error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={[
        'demo-file-selector',
        isLoading && 'demo-file-selector--loading',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <ComboBox
        value={selectedId}
        options={options}
        placeholder='Demo files'
        onChange={handleChange}
        disabled={isLoading}
      />
      {isLoading && (
        <span className='demo-file-selector__spinner' aria-hidden='true' />
      )}
    </div>
  );
};
